/** RFC 9457 problem body as emitted by the platform API (errors[] carries JSON pointers). */
export interface ProblemDetails {
  type?: string | null;
  title?: string | null;
  status?: number | null;
  detail?: string | null;
  instance?: string | null;
  code?: string | null;
  errors?: { code: string; pointer?: string | null; message: string; severity?: string | null }[] | null;
}

/** Error thrown for non-2xx API responses; carries the RFC 9457 problem body when present. */
export class ApiError extends Error {
  readonly status: number;
  readonly problem: ProblemDetails;

  constructor(status: number, problem: ProblemDetails) {
    super(problem.detail ?? problem.title ?? `Request failed (${status})`);
    this.name = 'ApiError';
    this.status = status;
    this.problem = problem;
  }

  /** Field errors keyed by JSON pointer, for mapping onto forms. */
  get fieldErrors(): Record<string, string> {
    const out: Record<string, string> = {};
    for (const e of this.problem.errors ?? []) if (e.pointer) out[e.pointer] = e.message;
    return out;
  }
}

function isProblem(v: unknown): v is ProblemDetails {
  return typeof v === 'object' && v !== null && ('title' in v || 'detail' in v || 'status' in v);
}

export function toApiError(status: number, body: unknown): ApiError {
  return new ApiError(status, isProblem(body) ? body : { status, title: `Request failed (${status})` });
}

/**
 * Unwraps an openapi-fetch result: returns data or throws ApiError.
 * Usage: `const tenants = unwrap(await api.GET('/v1/tenants'))`.
 */
export function unwrap<T>(result: { data?: T; error?: unknown; response: Response }): T {
  if (result.error !== undefined || !result.response.ok) throw toApiError(result.response.status, result.error);
  return result.data as T;
}

export function errorMessage(err: unknown): string {
  if (err instanceof ApiError) return err.problem.detail ?? err.problem.title ?? err.message;
  if (err instanceof Error) return err.message;
  return 'Unexpected error';
}
